import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../api/endpoints";
import type { AdminUser, Role, UserRole } from "../../types/domain";
import { useSession } from "../../hooks/useSession";
import { userDisplayName, userInitials } from "../../lib/userAvatar";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { IcoClose, IcoTrash } from "../../components/icons";

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days} дн назад`;
  return d.toLocaleDateString("ru-RU");
}

export function AdminPage() {
  const me = useSession().data ?? null;
  const qc = useQueryClient();
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [pendingRemoveError, setPendingRemoveError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await adminApi.listUsers()).users,
  });
  const rolesQuery = useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => (await adminApi.listRoles()).roles,
    staleTime: 60_000,
  });
  const roles = rolesQuery.data ?? [];

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) => adminApi.setRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.remove(id),
    onSuccess: () => {
      setPendingRemoveId(null);
      setPendingRemoveError(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err: unknown) => {
      setPendingRemoveError(err instanceof Error ? err.message : "Не удалось удалить");
    },
  });

  const pendingRemove = usersQuery.data?.find((u) => u.id === pendingRemoveId) ?? null;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Пользователи</h1>
          <div className="admin-sub">
            Выдача ролей и управление доступом. Новые логины через SSO попадают в статус
            «Ожидает» — откройте их и назначьте роль.
          </div>
        </div>
      </div>

      {usersQuery.isLoading && <div className="admin-empty">Загружаем список…</div>}
      {usersQuery.isError && (
        <div className="admin-empty admin-error">
          Не удалось загрузить пользователей. {String(usersQuery.error)}
        </div>
      )}
      {usersQuery.data && usersQuery.data.length === 0 && (
        <div className="admin-empty">Пока никого нет.</div>
      )}

      {usersQuery.data && usersQuery.data.length > 0 && (
        <div className="admin-table">
          <div className="admin-row admin-row-head">
            <div>Пользователь</div>
            <div>Роль</div>
            <div>Был в системе</div>
            <div />
          </div>
          {usersQuery.data.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              roles={roles}
              isSelf={me?.id === u.id}
              disabled={roleMutation.isPending || deleteMutation.isPending}
              onChangeRole={(role) => roleMutation.mutate({ id: u.id, role })}
              onRemove={() => {
                setPendingRemoveId(u.id);
                setPendingRemoveError(null);
              }}
            />
          ))}
        </div>
      )}

      {roleMutation.isError && (
        <div className="admin-error" style={{ marginTop: 12 }}>
          Не удалось изменить роль: {String(roleMutation.error)}
        </div>
      )}

      {pendingRemove && (
        <ConfirmRemove
          user={pendingRemove}
          busy={deleteMutation.isPending}
          error={pendingRemoveError}
          onCancel={() => {
            setPendingRemoveId(null);
            setPendingRemoveError(null);
          }}
          onConfirm={() => deleteMutation.mutate(pendingRemove.id)}
        />
      )}
    </div>
  );
}

interface UserRowProps {
  user: AdminUser;
  roles: Role[];
  isSelf: boolean;
  disabled: boolean;
  onChangeRole: (role: UserRole) => void;
  onRemove: () => void;
}

function UserRow({ user, roles, isSelf, disabled, onChangeRole, onRemove }: UserRowProps) {
  const [imgFailed, setImgFailed] = useState(false);
  // Если roles ещё не загружены — показываем хотя бы текущую роль юзера
  // как один пункт, чтобы select был валидным.
  const optionList = roles.length
    ? roles
    : ([{ id: user.role, name: user.role }] as Array<Pick<Role, "id" | "name"> & Partial<Role>>);
  const currentRole = roles.find((r) => r.id === user.role);
  const title = currentRole?.description ?? "";
  return (
    <div className={"admin-row admin-row-user" + (user.role === "pending" ? " pending" : "")}>
      <div className="admin-user-cell">
        {user.picture && !imgFailed ? (
          <img
            className="admin-avatar"
            src={user.picture}
            alt={userDisplayName(user.name, user.email)}
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="admin-avatar admin-avatar-fallback">
            {userInitials(user.name, user.email)}
          </div>
        )}
        <div>
          <div className="admin-user-name">
            {userDisplayName(user.name, user.email)}
            {isSelf && <span className="admin-self-tag">это вы</span>}
          </div>
          <div className="admin-user-email">{user.email}</div>
        </div>
      </div>

      <div className="admin-role-cell">
        <select
          className={"admin-role-select role-" + user.role}
          value={user.role}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next !== user.role) onChangeRole(next);
          }}
          title={title}
        >
          {optionList.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      <div className="admin-lastseen-cell">{formatRelative(user.lastSeen)}</div>

      <div className="admin-actions-cell">
        {!isSelf && (
          <button
            type="button"
            className="admin-remove-btn"
            onClick={onRemove}
            disabled={disabled}
            title="Удалить пользователя"
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}

interface ConfirmRemoveProps {
  user: AdminUser;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmRemove({ user, busy, error, onCancel, onConfirm }: ConfirmRemoveProps) {
  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="launch-dot" style={{ background: "#FEE4E2" }}>
            <IcoTrash size={12} stroke="#DC2626" />
          </div>
          <h2>Удалить пользователя?</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onCancel}>
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
            Аккаунт <b>{user.email}</b> потеряет доступ. Записи, которые он редактировал,
            останутся — поле «кем изменено» обнулится.
          </p>
          {error && (
            <div className="admin-error" style={{ marginTop: 10 }}>
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          <button type="button" className="btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Удаляем…" : "Удалить безвозвратно"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
